import BaseDatePicker from './BaseDatePicker';
import { assertIsoTimestamp, IsoTimestamp } from '#datetime';
import { Box } from '@mui/material';
import { PickersDay, PickersDayProps, pickersDayClasses } from '@mui/x-date-pickers/PickersDay';
import { addDays, differenceInMinutes } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { bind, unbind } from 'mousetrap';
import { useCallback, useEffect, useState } from 'react';

export type Props = {
  /**
   * These dates are the ones that are selected.
   * Dates in UTC that represent the start of day in `timezone`
   * The dates passed are converted to midnight time for the timezone if not already.
   * E.g. `2022-02-23T18:30:00.000Z` is midnight `2022-02-24T00:00:00.000Z` in Asia/Calcutta
   */
  selectedDates: IsoTimestamp[];

  /**
   * Timezone is used to display dates formatted in the correct timezone.
   */
  timezone: string;

  /**
   * Dates before this are disabled
   */
  minDate?: IsoTimestamp;

  /**
   * Dates after this are disabled
   */
  maxDate?: IsoTimestamp;

  disablePast?: boolean;

  disableFuture?: boolean;

  /**
   * If true, the time component of the date will be preserved when the date is changed.
   */
  shouldPreserveTime?: boolean;

  shouldDisableDate?: (day: IsoTimestamp) => boolean;

  /**
   * The dates are start of day for the time represented by `timezone`
   * E.g.
   *   - `2022-02-23T18:30:00.000Z` is midnight in Asia/Calcutta
   *   - `2022-02-23T08:00:00.000Z` is midnight in America/Los_Angeles
   */
  onSelectedDatesChanged: (newDates: IsoTimestamp[]) => void;

  onMonthChange?: (newMonth: IsoTimestamp) => void;

  renderDay?: (props: PickersDayProps<IsoTimestamp>) => JSX.Element;
};

const MultipleDatePicker = ({
  selectedDates,
  timezone,
  minDate,
  maxDate,
  disablePast,
  disableFuture,
  shouldPreserveTime,
  renderDay,
  onMonthChange,
  onSelectedDatesChanged,
  shouldDisableDate,
}: Props): JSX.Element => {
  const [shiftPressed, setShiftPressed] = useState(false);

  const handleOnSelectedDatesChanged = useCallback(
    (newDate: IsoTimestamp) => {
      if (shiftPressed && selectedDates.length > 0) {
        const sortedSelectedDates = [...selectedDates].sort();
        const index = sortedSelectedDates.findIndex((date) => date === newDate);

        if (index === -1) {
          // if date is not present then select all dates between the last selected date and this date
          const dateToPrefillFrom = [...sortedSelectedDates].reverse().find((selectedDate) => {
            return newDate > selectedDate;
          });

          const datesToFill: IsoTimestamp[] = [];
          if (dateToPrefillFrom) {
            let nextDate = dateToPrefillFrom;
            while (nextDate < newDate) {
              nextDate = assertIsoTimestamp(addDays(nextDate, 1).toISOString());
              datesToFill.push(nextDate);
            }
          }
          onSelectedDatesChanged([...sortedSelectedDates, ...datesToFill]);
        } else {
          // if date is present then remove all dates between the last selected date and this date
          const subArray = sortedSelectedDates.slice(0, index);
          let dateCopy = newDate;

          while (subArray.length) {
            const lastDate = subArray.pop();
            if (lastDate) {
              const diff = differenceInMinutes(dateCopy, lastDate);

              // The 1380 and 1500 are the minutes difference between the dates when the DST shift happens.
              if (diff === 1440 || diff === 1380 || diff === 1500) {
                dateCopy = lastDate;
              }
            } else {
              break;
            }
          }

          const dateToDeselectFromIndex = sortedSelectedDates.indexOf(dateCopy);
          onSelectedDatesChanged([
            ...sortedSelectedDates.slice(0, dateToDeselectFromIndex),
            ...sortedSelectedDates.slice(index + 1),
          ]);
        }
      } else if (selectedDates.includes(newDate)) {
        onSelectedDatesChanged(selectedDates.filter((date) => date !== newDate));
      } else {
        onSelectedDatesChanged([...selectedDates, newDate]);
      }
    },
    [onSelectedDatesChanged, selectedDates, shiftPressed]
  );

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    // Listen for shift key press so that we can select multiple dates in a single go.
    bind(['shift'], () => setShiftPressed(true), 'keydown');
    bind(['shift'], () => setShiftPressed(false), 'keyup');

    return () => {
      unbind(['shift'], 'keydown');
      unbind(['shift'], 'keyup');
    };
  }, []);

  const renderDayWrapper = useCallback(
    (props: PickersDayProps<IsoTimestamp>) => {
      const selected = selectedDates.includes(props.day);

      /**
       * The date we get from <BaseDatePicker /> is in the selected timezone.
       * We need to convert what the date would look like in selected timezone in local timezone for rendering.
       * Hence the below conversion.
       */
      const day = assertIsoTimestamp(toZonedTime(props.day, timezone).toISOString());

      if (!renderDay) {
        return (
          <Box flexGrow={1} display="flex" alignItems="center" justifyContent="center">
            <PickersDay
              {...props}
              day={day}
              selected={selected}
              // eslint-disable-next-line no-restricted-syntax
              sx={{
                ...props.sx,
                width: '40px',
                height: '40px',
                [`&.${pickersDayClasses.root}`]: {
                  flexGrow: 0,
                },
              }}
            />
          </Box>
        );
      }

      return renderDay({
        ...props,
        selected,
        day: assertIsoTimestamp(props.day),
      });
    },
    [renderDay, selectedDates, timezone]
  );

  return (
    <BaseDatePicker
      disablePast={disablePast}
      disableFuture={disableFuture}
      shouldPreserveTime={shouldPreserveTime}
      defaultDate={selectedDates[0]}
      timezone={timezone}
      minDate={minDate}
      maxDate={maxDate}
      renderDay={renderDayWrapper}
      onMonthChange={onMonthChange}
      onChange={handleOnSelectedDatesChanged}
      shouldDisableDate={shouldDisableDate}
    />
  );
};

export default MultipleDatePicker;
